import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { HIDE_ERROR_NOTIFICATIONS } from "../core/token-interceptor.service";
import { GetShipmentListPayload } from "../models/shipment.model";
import { EditShipmentSkuRequest, ShipmentSkuResolvedPayload } from "../models/shipyard.model";
import {
  CreateWalmartDraftShipmentPayload,
  CreateWalmartShipmentPayload,
  CreateWLabelsPayload,
  RemoveWalmartSkusPayload,
  WalmartCreateSku,
} from "../models/walmart.model";
import { AgGridRequestService } from "./ag-grid-request.service";
import { HeaderService } from "../components/header/header.service";

@Injectable({
  providedIn: "root",
})
export class WalmartService implements OnDestroy{
  private url = "api/Walmart";
  private destroy$ = new Subject<void>();
  account;
  private headers = new HttpHeaders({ "Content-Type": "application/json" });
  options = { headers: this.headers };

  constructor(private http: HttpClient,
    private agGridRequestService: AgGridRequestService,
    private headerService:HeaderService) {
      this.headerService.accountsSelected.subscribe((e) => (this.account = e[0]));
    }

  CreateWalmartShipment(
    createWalmartShipmentPayload: CreateWalmartShipmentPayload
  ) {
    const data = JSON.stringify(createWalmartShipmentPayload);
    return this.http.post(
      `/${this.url}/CreateWalmartShipment`,
      data,
      this.options
    );
  }

  CreateWShipmentDraft(
    CreateWalmartDraftShipmentPayload: CreateWalmartDraftShipmentPayload
  ) {
    const data = JSON.stringify(CreateWalmartDraftShipmentPayload);
    return this.http.post(
      `/${this.url}/CreateWShipmentDraft`,
      data,
      this.options
    );
  }

  PrintSku(data) {
    return this.http.post(`/${this.url}/PrintGtinByUser/`, data, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }
     
  public CreateWalmartShipmentLabels(
    palletLabelRequestModel: CreateWLabelsPayload
  ) {
    const data = JSON.stringify(palletLabelRequestModel);
    return this.http.post(`/${this.url}/CreateWalmartShipmentLabels`, data, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  RemoveWalmartSkus(RemoveWalmartSkusPayload: RemoveWalmartSkusPayload){    
    return this.http.put(
      `/${this.url}/UpdateShipmentQuantities`,
      RemoveWalmartSkusPayload,
      this.options
    );
  }

  ExportShipment(shipmentHeaderId: number): Observable<any> {
    return this.http.post(
      `/${this.url}/ExportWShipment/`,
      { shipmentHeaderId },
      {
        headers: this.headers,
        observe: "response",
        responseType: "blob",
      }
    );
  }

  DeleteShipmentDraft(shipmentHeaderId: number) {
    return this.http.post(
      `/${this.url}/DeleteWShipmentDraft`,
      { shipmentHeaderId },
      this.options
    );
  }

  SaveShipmentCompleted(shipmentHeaderID, completed) {
    return this.http.post(
      `/${this.url}/SaveWShipmentCompleted`,
      { shipmentHeaderID, completed },
      this.options
    );
  }

  GetShipmentDetail(shipmentId: number, refresh: boolean) {
    return this.http.get(
      `/${this.url}/GetWShipmentDetails?ShipmentHeaderID=${shipmentId}&IsRefresh=${refresh}`,
      {
        ...this.options,
        context: new HttpContext().set(HIDE_ERROR_NOTIFICATIONS, true),
      }
    );
  }

  GetShipments(
    account,
    startRow,
    endRow,
    statuses?: string[],
    ShipmentType?,
    data?: GetShipmentListPayload
  ) {
    return this.http.get(
      `/${
        this.url
      }/GetWShipmentList?Account=${account}&StartRow=${startRow}&EndRow=${endRow}${
        statuses.length
          ? statuses.map((status) => `&Statuses=${status}`).join("")
          : ""
      }${data && data.shipmentId ? "&ShipmentId=" + data.shipmentId : ""}${
        data && data.containSkus ? "&HasSku=" + data.containSkus : ""
      }
      ${
        data && data.hideDeleted ? "&HideDeleted=" + data.hideDeleted : ""
      }
      `,
      this.options
    );
  }

  DownloadShipments(account: string) {
    return this.http.get(
      `/${this.url}/DownloadWShipments?Account=${account}`,
      this.options
    );
  }

  CancelWalmartShipment(account,shipmentHeaderId){
    return this.http.delete(
      `/${this.url}/CancelWalmartShipment?Account=${account}&ShipmentHeaderId=${shipmentHeaderId}`,
      this.options
    );
  }

  EditShipmentSku(shipmentSku: EditShipmentSkuRequest) {
    return this.http.post(
      `/${this.url}/EditWShipmentSku/`,
      shipmentSku,
      {
        ...this.options,
        context: new HttpContext().set(HIDE_ERROR_NOTIFICATIONS, true),
      }
    );
  }

  ClearWShipment(shipmentHeaderId){
    return this.http.post(
      `/${this.url}/ClearWShipment`,
      { shipmentHeaderId },
      this.options
    );
  }
 
  SetSkuResolved(payload:ShipmentSkuResolvedPayload) {
    return this.http.post(
      `/${this.url}/SetWShipmentSkuResolved/`,
      payload,
      this.options
    );
  }
  GetShipmentDetailList(shipmentHeaderId) {
    return this.http.get(
      `/${this.url}/GetWalmartShipmentDetailList?ShipmentHeaderID=${shipmentHeaderId}`,this.options
    );
  }


  fetchInboundPreview(payload){
    return this.http.post(`/${this.url}/FetchInboundPreview`, payload, this.options)};

  CreateMultiWShipment(createWalmartShipmentPayload: CreateWalmartShipmentPayload){
    const data = JSON.stringify(createWalmartShipmentPayload);
    return this.http.post(
      `/${this.url}/CreateMultiWShipment`,
      data,
      this.options
    );

  }

  CheckValidationForNegativeQty(payload) {
    return this.http.post(
      `/${this.url}/CheckWValidationForNegativeQty`, payload, this.options
    );
  }

  MoveShipmentToDraft(payload) {
    return this.http.post(`/${this.url}/MoveWShipmentToDraft/`, payload, this.options);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  walmartShipmentHeaderList(body, columnDefs, globalSearch?,account?,sumColumnDefs?) {
    let filters;
    if(account){
      filters = {
        Account: {
          filterType: "set",
          values: account,
        },
      } 
    }
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columnDefs,
      globalSearch,
      filters
    );
    return this.http.post(`/${this.url}/WalmartShipmentHeaderList`, payload, this.options);
  }

  walmartShipmentHeaderListExport(filters, columns, globalSearch,isExport?){
    const body = {
      filterModel: filters,     
    };

    if (this.account) {
      filters["Account"] = {
        filterType: "set",
        values: [this.account],
      };
    }
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columns,
      globalSearch,
      null,
      null,
      isExport
    );

    return this.http.post(`/${this.url}/WalmartShipmentHeaderListExport`, payload, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  GetCatalogByGtin(payload) {
    return this.http.get(`/${this.url}/GetCatalogByGtin?Account=${payload.account}&gtin=${payload.gtin}`, this.options)
  }

  CreateWalmartSku(payload: WalmartCreateSku) {
    return this.http.post(`/${this.url}/CreateWalmartSku`, payload, this.options)
  }

  addDeductFromInventory(payload){
    return this.http.post(`/${this.url}/WShipmentDeduction`,payload,this.options);
  }
}
